import React, {
  CSSProperties,
  forwardRef,
  PropsWithChildren,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import { throttle } from 'lodash';
import './index.css';

export default forwardRef(
  (
    props: PropsWithChildren<{
      direction?: string;
      className?: string;
      style?: CSSProperties;
      scrollEnabled?: boolean; // 嵌套滚动组件是否可滚动 TODO
      showScrollbar?: boolean;
      pagingEnabled?: boolean; // 当内容部分是 ViewPager 时，需要设置为 true。可选(default： false)
      bounces?: boolean; // 是否开启回弹动画，可选（default: true TODO）
      stickyHeight?: number;
      onStickyChange?: (e?: any) => void;
      // showScrollIndicator?: boolean, TODO
      offset?: number;
      onClick?: (e?: any) => void;
      onScroll?: (e?: any) => void;
      onScrollStart?: (e?: any) => void;
      onScrollEnd?: (e?: any) => void;
    }>,
    ref
  ) => {
    const scrollerRef = useRef<HTMLDivElement>(null);
    const innerRef = useRef<HTMLDivElement>(null);
    const headerRef = useRef<HTMLDivElement>(null) as React.MutableRefObject<HTMLDivElement | null>;
    const contentRef = useRef<HTMLDivElement>(null) as React.MutableRefObject<HTMLDivElement | null>;
    const headerPlaceholderRef = useRef<HTMLDivElement>(null);

    const startScrollRef = useRef(false);
    const startScrollTimerRef = useRef<number>(null) as React.MutableRefObject<number | null>;
    const isSticky = useRef<boolean>(false);

    const scrollerClassName = useMemo(() => {
      const { className } = props;
      const classNames = ['custom-mach-nested-scroll'];
      if (className) {
        classNames.push(className);
      }
      return classNames.join(' ');
    }, [props]);

    const scrollerStyle = useMemo(() => {
      const { style } = props;
      return {
        ...style,
      };
    }, [props]);

    const _initDom = () => {
      const innerDom = innerRef.current;
      if (!innerDom) return;
      headerRef.current = innerDom.querySelector('[data-role="header"]');
      contentRef.current = innerDom.querySelector('[data-role="content"]');
    };

    const _setIsSticky = (value: boolean) => {
      if (isSticky.current === value) return;
      isSticky.current = value;
      props.onStickyChange?.({ isSticky: value });
    };

    const _handleSticky = () => {
      const { stickyHeight } = props;
      const headerDom = headerRef.current;
      const scrollerDom = scrollerRef.current;
      const headerPlaceholderDom = headerPlaceholderRef.current;

      if (!stickyHeight || !headerDom || !scrollerDom || !headerPlaceholderDom) return;

      const headerHeight = headerDom.clientHeight;
      const scrollTop = scrollerDom.scrollTop;
      const curSticky = scrollTop >= headerHeight - stickyHeight;

      if (curSticky === isSticky.current) return;

      if (curSticky) {
        headerDom.style.position = 'absolute';
        headerDom.style.zIndex = '10';
        headerDom.style.top = `-${headerHeight - stickyHeight}px`;
        headerDom.style.left = '0';
        headerDom.style.right = '0';
        headerPlaceholderDom.style.height = `${headerHeight}px`;
      } else {
        headerDom.style.position = 'relative';
        headerDom.style.zIndex = '0';
        headerDom.style.top = '0';
        headerDom.style.left = '';
        headerDom.style.right = '';
        headerPlaceholderDom.style.height = '0';
      }
      _setIsSticky(curSticky);
    };

    const _handleScroll = (e: any) => {
      startScrollTimerRef.current && clearTimeout(startScrollTimerRef.current);
      const targetDom = e.target;
      if (!targetDom) return;
      const {
        scrollHeight, scrollLeft, scrollWidth, scrollTop, clientHeight
      } = targetDom || {};
      const params = {
        scrollHeight,
        scrollLeft,
        scrollWidth,
        scrollTop,
        isBottom: scrollTop + clientHeight >= scrollHeight - 1, // 已滚动距离，
        contentOffset: {
          x: scrollLeft,
          y: scrollTop,
        },
      };
      if (!startScrollRef.current) {
        startScrollRef.current = true;
        props.onScrollStart?.(params);
      }
      props?.onScroll?.(params);
      startScrollTimerRef.current = setTimeout(() => {
        startScrollRef.current = false;
        props.onScrollEnd?.(params);
        clearTimeout(startScrollTimerRef.current as number);
      }, 100);
      _handleSticky();
    };

    const _handleClick = (e: any) => {
      props?.onClick?.(e);
    };

    const collapseHeader = () => {
      const headerHeight = headerRef.current?.getBoundingClientRect().height;
      if (!headerHeight || !props.stickyHeight) return;
      scrollerRef.current?.scrollTo({
        top: headerHeight - props.stickyHeight,
        left: 0,
        behavior: 'auto',
      });
    };

    const expandHeader = () => {
      scrollerRef.current?.scrollTo({
        top: 0,
        left: 0,
        behavior: 'auto',
      });
    };

    useEffect(() => {
      _initDom();
    }, []);

    useImperativeHandle(ref, () => ({
      collapseHeader,
      expandHeader,
    }));

    return (
      <div
        className={scrollerClassName}
        style={scrollerStyle}
        ref={scrollerRef}
        onScroll={_handleScroll}
        onClick={_handleClick}
      >
        <div className="custom-mach-nested-scroll_inner" ref={innerRef}>
          <div ref={headerPlaceholderRef} className="custom-mach-nested-scroll_inner-placeholder" />
          {props.children}
        </div>
      </div>
    );
  }
);
